/**
 * The one place the front end names a role.
 *
 * This mirrors `api/app/Models/User.php`. It is not authoritative -- the API's
 * `role:` middleware and each controller's `assertRole()` are -- so treat every
 * list here as what the UI should *offer*, never as a security boundary. The
 * point of the file is that there is now a single copy to update: these arrays
 * used to be hand-duplicated in App.js, AdminSidebar.js and services/api.js,
 * with a fourth stale copy in a components/auth/AdminNav.js that nothing even
 * imported (deleted), and nothing kept the four in step.
 *
 * See api/docs/role-access-matrix.md for what each role is meant to reach.
 */

export const ROLES = {
  SUPER_ADMIN: "super_admin",
  ADMIN: "admin",
  MANAGER: "manager",
  RESELLER_ADMIN: "reseller_admin",
  SALES: "sales",
  FINANCIAL_CASH: "financial_cash",
  FINANCIAL_BANK: "financial_bank",
  ADMIN_PRICE_CHANGE: "admin_price_change",
  IMAGE_UPLOAD: "image_upload",
  WAREHOUSE: "warehouse",
  STAFF_VIEWER: "staff_viewer",
  USER: "user"
};

/** Everything that reaches /admin at all. Mirrors User::staffRoles(). */
export const STAFF_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.SALES,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK,
  ROLES.ADMIN_PRICE_CHANGE,
  ROLES.IMAGE_UPLOAD,
  ROLES.WAREHOUSE,
  ROLES.STAFF_VIEWER
];

/** Staff plus the customer: every role that may hold a session here. */
export const LOGIN_ROLES = [...STAFF_ROLES, ROLES.USER];

export const ADMIN_ROLES = STAFF_ROLES;
export const MANAGEMENT_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.RESELLER_ADMIN];
export const ORDER_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.SALES,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK,
  ROLES.RESELLER_ADMIN
];
/**
 * Reading a customer order -- the list section and the detail page both. Mirrors
 * OrderController::index/show, which were widened to these six so a role could no
 * longer PATCH an order it was refused a read on. The UI kept the old two-role list
 * for a while afterwards, which meant the financial reviewers still could not open an
 * order to approve its payment.
 */
export const ORDER_VIEW_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK,
  ROLES.RESELLER_ADMIN
];
export const CUSTOMER_ORDER_EDIT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK,
  ROLES.RESELLER_ADMIN
];
export const RESELLER_ORDER_EDIT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK
];
/** Mirrors User::resellerPurchaseOrderInvoicePrintRoles(). */
export const RESELLER_PO_INVOICE_PRINT_ROLES = RESELLER_ORDER_EDIT_ROLES;
export const RESELLER_PURCHASE_ORDER_ROLES = ORDER_ROLES;
/** Min/max stock per size in a reseller's store (ResellerVariantLimitController). */
export const RESELLER_STOCK_LIMIT_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN];
/** Placing a restock order: the reseller themselves (ResellerPurchaseOrderStoreRequest). */
export const RESELLER_PURCHASE_ORDER_CREATE_ROLES = [ROLES.RESELLER_ADMIN];

/**
 * Read the catalog. `warehouse` and `staff_viewer` are here but deliberately
 * absent from PRODUCT_EDIT_ROLES: the API answers them an allowlisted payload
 * with no price at all (`warehouse` additionally gets stock), so the list and
 * detail pages must not render price columns or an edit affordance for them.
 */
export const PRODUCT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.SALES,
  ROLES.RESELLER_ADMIN,
  ROLES.ADMIN_PRICE_CHANGE,
  ROLES.IMAGE_UPLOAD,
  ROLES.WAREHOUSE,
  ROLES.STAFF_VIEWER
];
export const PRODUCT_MANAGE_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER];
export const PRODUCT_EDIT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.ADMIN_PRICE_CHANGE,
  ROLES.IMAGE_UPLOAD
];
export const CATEGORY_MANAGE_ROLES = PRODUCT_MANAGE_ROLES;
/** The reseller-stock overview -- reseller business data, not factory stock. */
export const INVENTORY_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER];
/**
 * Recording central-warehouse stock in and out. Mirrors
 * InventoryController::storeMovement -- this is the factory's own stock, which is
 * why `warehouse` is here and absent from INVENTORY_ROLES above.
 */
export const STOCK_MOVEMENT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.WAREHOUSE
];
export const NOTIFICATION_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.SALES
];
export const REPORTS_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.SALES,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK
];
// Resellers no longer see the contact-request screen (client request, WA-130); the API is unchanged.
export const LEAD_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER];
export const DASHBOARD_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.SALES,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK
];
/** Own-account email / WhatsApp settings: the platform's, or a reseller's own. */
export const MESSAGING_SETTINGS_ROLES = [ROLES.SUPER_ADMIN, ROLES.RESELLER_ADMIN];
export const SUPER_ADMIN_ONLY = [ROLES.SUPER_ADMIN];

/**
 * The roles a super_admin or admin may pick in the user forms, in the order they are
 * offered. `super_admin` and `admin` are omitted on purpose: those two options are
 * rendered conditionally by the pages themselves, since an admin may only keep an
 * existing one, never grant it.
 *
 * Note StaffUserSeeder is the source of truth for the client's 41 staff accounts and
 * resyncs `role` on every run -- a change made here is reverted at the next seed. See
 * api/docs/role-access-matrix.md.
 */
export const ASSIGNABLE_ROLES = [
  ROLES.MANAGER,
  ROLES.RESELLER_ADMIN,
  ROLES.SALES,
  ROLES.FINANCIAL_CASH,
  ROLES.FINANCIAL_BANK,
  ROLES.ADMIN_PRICE_CHANGE,
  ROLES.IMAGE_UPLOAD,
  ROLES.WAREHOUSE,
  ROLES.STAFF_VIEWER,
  ROLES.USER
];

/** Every role, for a filter dropdown that must be able to name what it is filtering. */
export const ALL_ROLES = Object.values(ROLES);

/**
 * Where a role lands after login, as a path relative to PUBLIC_URL. Anyone the
 * API's dashboard endpoint refuses (DASHBOARD_ROLES) must land somewhere else,
 * or their first screen is a 403.
 */
export const ROLE_HOME_PATH = {
  [ROLES.SUPER_ADMIN]: "/admin",
  [ROLES.ADMIN]: "/admin",
  [ROLES.MANAGER]: "/admin",
  [ROLES.RESELLER_ADMIN]: "/admin",
  [ROLES.SALES]: "/admin",
  [ROLES.FINANCIAL_CASH]: "/admin",
  [ROLES.FINANCIAL_BANK]: "/admin",
  [ROLES.ADMIN_PRICE_CHANGE]: "/admin/products",
  [ROLES.IMAGE_UPLOAD]: "/admin/products",
  [ROLES.WAREHOUSE]: "/admin/stock",
  [ROLES.STAFF_VIEWER]: "/admin/products",
  [ROLES.USER]: "/customer/dashboard"
};

export const hasRole = (role, allowed) => allowed.includes(role);

/**
 * The display label for a role, e.g. "super_admin" -> "Superadministrador" (es)
 * / "Super Admin" (en). Backed by admin.json's `role_*` keys (see the 2026-09
 * Spanish/English translation audit, §8); falls back to a title-cased version
 * of the raw value for anything not yet in the glossary.
 */
export const roleLabel = (t, role) => {
  const key = String(role || "");
  if (!key) return "";
  const fallback = key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  return t(`role_${key}`, { defaultValue: fallback });
};
